import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs'
import SuccessAlert from '../../components/Alerts/SuccessAlert'
import FailAlert from '../../components/Alerts/FailAlert'
import { useNavigate, useParams } from 'react-router-dom'
import { ALL_STORES, UpdateStore, useStore, useStoreByLink } from '../../graphql'
import { useMutation } from '@apollo/client'
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import LoadingWidget from '../../components/LoadingWidget/LoadingWidget'
import { t } from 'i18next'

const EditStore = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [fail, setFail] = useState(false);
  const [failMsg, setFailMsg] = useState<any>("");

  // i had to use a main state for control loading because of ui issues
  const [initialLoading, setInitialLoading] = useState(true);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");

  const [store, { loading, data: dataStore }] = useStore();

  useEffect(() => {
    (async () => {
      await store({
        variables: {
          storeId: id
        }
      });

      setInitialLoading(false)
    })()
  }, [store]);

  useEffect(() => {
    if (dataStore) {
      setName(dataStore.store.name);
      setAddress(dataStore.store.address);
      setDescription(dataStore.store.description);
      setLink(dataStore.store.link);
      setSelectedPreviewImageUrl(dataStore?.store.logo)
    }
  }, [dataStore])

  const [storeByLink, { data: dataStoreByLink }] = useStoreByLink();

  useEffect(() => {
    if (link !== "" && link !== dataStore?.store.link) {
      storeByLink({
        variables: { link: link }
      })
    }
  }, [link]);

  const [linkUsedError, setLinkUsedError] = useState<boolean>(false)

  useEffect(() => {
    if (link === dataStore?.store.link) {
      setLinkUsedError(false);
    }

    if (dataStoreByLink?.storeByLink) {
      setLinkUsedError(true)
    } else {
      setLinkUsedError(false)
    }

  }, [dataStoreByLink, dataStore, link]);

  // update
  const [updateStore, { loading: updateLoading }] = useMutation(UpdateStore, {
    onError: (error) => {
      // @ts-ignore
      const code = error.networkError?.result?.errors?.[0]?.extensions?.code;
      
      if (code == "LINK_ALREADY_EXISTS") {
        setFailMsg(t("Link already used before"));
        setFail(true);
        return
      }

      console.error(code);
    },
    onCompleted: () => {
      setSuccessMsg(t('store updated successfully'));
      setSuccess(true);

      window.setTimeout(() => {
        navigate(`/stores`);
      }, 1000);
    },
    refetchQueries: [{ query: ALL_STORES }]
  });

  // File input
  const [selectedPreviewImageUrl, setSelectedPreviewImageUrl] = useState("");
  const [imageToCrop, setImageToCrop] = useState<any>();
  const [cropPopup, setCropPopup] = useState(false);

  // For the api
  const [selectedImage, setSelectedImage] = useState<File | null>();

  const cropperRef = useRef<any>();

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (files && files.length > 0) {
      const urls = Array.from(files).map((file) => URL.createObjectURL(file));

      setImageToCrop(urls[0]);
      setCropPopup(true)
    }
  };

  const handleCrop = () => {
    if (cropperRef.current) {

      if (typeof cropperRef.current.cropper.getCroppedCanvas() === 'undefined') {
        return;
      }
      const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas();
      const croppedImageUrl = cropperRef.current.cropper.getCroppedCanvas().toDataURL();
      setSelectedPreviewImageUrl(croppedImageUrl);

      croppedCanvas.toBlob((blob: any) => {
        const file = new File([blob], "cropped-image.png", { type: "image/png" });
        setSelectedImage(file);
      });
      setCropPopup(false);

    }
  };

  const handlePopupClose = () => {
    setImageToCrop("");
    setCropPopup(false);
  };

  const submit = async (e: any) => {
    e.preventDefault();

    if (name === "") {
      setFailMsg(t('NameRequire'));
      setFail(true);
      return
    }

    if (address === "") {
      setFailMsg(t('haveToSelectImage'));
      setFail(true);
      return
    }

    if (description === "") {
      setFailMsg(t('descriptionRequire'));
      setFail(true);
      return
    }

    if (link === "") {
      setFailMsg(t('linkRequire'));
      setFail(true);
      return
    }

    if (!linkUsedError) {
      if (selectedImage !== undefined) {
        await updateStore({
          variables: {
            updateStoreId: id,
            content: {
              logo: selectedImage,
              address: address,
              description: description,
              link: link,
              name: name
            }
          }
        })
      } else {
        await updateStore({
          variables: {
            updateStoreId: id,
            content: {
              address: address,
              description: description,
              link: link,
              name: name
            }
          }
        })
      }
    }
  }

  return (
    <section className='my-4 mx-7'>
      {success && <SuccessAlert success={success} setSuccess={setSuccess} successMsg={successMsg} />}

      {fail && <FailAlert fail={fail} setFail={setFail} failMsg={failMsg} />}

      {cropPopup && (
        <div className='fixed inset-0 z-50 flex items-center justify-center w-full min-h-screen py-6 bg-white/30 backdrop-blur-md'>
          <div className='relative w-full max-h-full p-3 overflow-y-auto bg-white border md:w-5/6'>
            <Cropper
              ref={cropperRef}
              src={imageToCrop}
              style={{ height: 400, width: '100%' }}
            />

            <div className='flex justify-end gap-2 mt-3'>
              <button onClick={handleCrop} className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-900 focus:z-10">{t('cropImage')}</button>
              <button onClick={handlePopupClose} className="px-3 py-2 text-sm font-medium text-center text-white bg-red-600 rounded-lg hover:bg-red-700">{t('close')}</button>
            </div>
          </div>
        </div>
      )}

      <div className='flex flex-col-reverse md:flex-row md:justify-between md:items-center'>
        <div>
          <h3 className='text-2xl font-semibold'>{t('Edit Store')}</h3>
        </div>
        <div>
          <Breadcrumbs />
        </div>
      </div>

      {loading || initialLoading ?
        <LoadingWidget />
        :
        <form className='relative p-2 mt-3 bg-white rounded-md'>
          {updateLoading &&
            <div className='absolute top-0 left-0 z-30 flex items-center justify-center w-full h-full py-10 my-2 bg-white/70'>
              <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
              </svg>
            </div>
          }

          <div className="mb-4">
            <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">{t('name')}</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} id="name" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('Store Name')} />
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900">{t('logo')}</label>
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col w-full h-32 border-2 border-dashed rounded-md cursor-pointer hover:bg-gray-100 hover:border-gray-300">
                <div className="flex flex-col items-center justify-center pt-7">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-gray-400 group-hover:text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                  <p className="pt-1 text-sm tracking-wider text-gray-400 group-hover:text-gray-600">
                    {t('selectPhoto')}
                  </p>
                </div>
                <input type="file" onChange={handleFileSelect} className="opacity-0" multiple={true} />
              </label>
            </div>

            <div className="flex flex-wrap gap-2 mt-2">
              {selectedImage !== undefined ?
                <div className="relative overflow-hidden">
                  <img className="w-20 h-20 rounded-md" src={selectedPreviewImageUrl} alt="" />
                </div>
                :
                selectedPreviewImageUrl !== "" &&
                <div className="relative overflow-hidden">
                  <img className="w-20 h-20 rounded-md" src={`https://store-api.qafilaty.com/images/${selectedPreviewImageUrl}`} alt="" />
                </div>
              }
            </div>
          </div>

          <div className="mt-2 mb-4">
            <label htmlFor="address" className="block mb-2 text-sm font-medium text-gray-900">{t('address')}</label>
            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} id="address" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('store address')} />
          </div>

          <div className='my-2'>
            <label htmlFor="description" className="block mb-2 text-sm font-medium text-gray-900">{t('description')}</label>
            <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} name="message" className="w-full h-32 px-3 py-1 text-base leading-6 text-gray-700 transition-colors duration-200 ease-in-out bg-white border border-gray-300 rounded outline-none resize-none focus:border-primary"></textarea>
          </div>

          <div className="mb-4">
            <label htmlFor="link" className="block mb-2 text-sm font-medium text-gray-900">{t('link')}</label>
            <input type="text" value={link} onChange={(e) => setLink(e.target.value)} id="link" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('Store Link')} />
            <div>
              {linkUsedError === true &&
                <p className='text-sm font-semibold text-red-600'>{t('Link already used before')}</p>
              }
            </div>
          </div>

          <div className='flex justify-end pt-5'>
            <button onClick={(e) => submit(e)} className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
              </svg>

              <span className="mx-2">
                {t('save')}
              </span>
            </button>
          </div>
        </form>
      }
    </section >
  )
}

export default EditStore